import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE, api, widgetKeyHeaders } from '../config/api';

const FrameworkPickerModal = ({
  isOpen,
  onClose,
  companyIdSlug,
  onFrameworkSelect
}) => {
  const [frameworks, setFrameworks] = useState([]);
  const [readyFrameworks, setReadyFrameworks] = useState([]);
  const [lockedFrameworks, setLockedFrameworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && companyIdSlug) {
      fetchFrameworks();
    }
  }, [isOpen, companyIdSlug]);

  const fetchFrameworks = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = api.coachTools(companyIdSlug);
      const response = await fetch(url, { headers: { ...widgetKeyHeaders() } });
      const data = await response.json();

      if (data.tools && Array.isArray(data.tools)) {
        setFrameworks(data.tools);
        setReadyFrameworks(data.tools.filter(t => t.ready === true));
        setLockedFrameworks(data.tools.filter(t => t.ready !== true));
      } else {
        setFrameworks([]);
        setReadyFrameworks([]);
        setLockedFrameworks([]);
      }
    } catch (e) {
      console.error('[FrameworkPicker] Error fetching frameworks:', e);
      setError('Failed to load frameworks');
      setFrameworks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFrameworkSelect = (framework) => {
    if (framework.ready) {
      onFrameworkSelect(framework);
      onClose();
    }
  };

  const formatNeedLabel = (need) => {
    return need
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="mm-modal-overlay" onClick={onClose}>
        <motion.div
          className="mm-framework-modal"
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
        >
          {/* Header */}
          <div className="mm-modal-header">
            <div className="mm-modal-title-row">
              <h2 className="mm-modal-title">Coaching Frameworks</h2>
              <button className="mm-modal-close" onClick={onClose} aria-label="Close">
                ✕
              </button>
            </div>
            <p className="mm-modal-subtitle">Choose the approach that fits your needs</p>
          </div>

          {/* Content */}
          <div className="mm-modal-content">
            {loading && (
              <div className="mm-loading-state">
                <div className="mm-spinner"></div>
                <p>Loading frameworks...</p>
              </div>
            )}

            {error && (
              <div className="mm-error-state">
                <p>{error}</p>
                <button className="mm-ghost-button" onClick={fetchFrameworks}>
                  Try Again
                </button>
              </div>
            )}

            {!loading && !error && (
              <>
                {/* All Frameworks - Show ready ones first */}
                {readyFrameworks.length > 0 && (
                  <div className="mm-frameworks-section">
                    <div className="mm-frameworks-grid">
                      {readyFrameworks.map((framework) => (
                        <motion.div
                          key={framework.id}
                          className="mm-framework-card mm-framework-ready"
                          onClick={() => handleFrameworkSelect(framework)}
                          whileHover={{ scale: 1.01, y: -2 }}
                          whileTap={{ scale: 0.99 }}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="mm-framework-card-header">
                            <div className={`mm-framework-icon-wrapper mm-icon-${(framework.id || '').toLowerCase().replace(/\s+/g, '-')}`}>
                              <div className="mm-framework-icon">{framework.icon || '🎯'}</div>
                            </div>
                          </div>
                          <h4 className="mm-framework-name">{framework.name}</h4>
                          {framework.description && (
                            <p className="mm-framework-description">{framework.description}</p>
                          )}
                          {framework.tool_config?.description && (
                            <p className="mm-framework-details">{framework.tool_config.description}</p>
                          )}
                          <div className="mm-framework-actions">
                            <button 
                              className={`mm-framework-start-btn mm-btn-${(framework.id || '').toLowerCase().replace(/\s+/g, '-')}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFrameworkSelect(framework);
                              }}
                            >
                              Start Session
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Locked Frameworks */}
                {lockedFrameworks.length > 0 && (
                  <div className="mm-frameworks-section">
                    <h3 className="mm-section-title">Unlock With Context</h3>
                    <div className="mm-frameworks-grid">
                      {lockedFrameworks.map((framework) => (
                        <motion.div
                          key={framework.id}
                          className="mm-framework-card mm-framework-locked"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div className="mm-framework-card-header">
                            <div className="mm-framework-icon mm-icon-locked">{framework.icon || '🎯'}</div>
                            <span className="mm-locked-badge">
                              🔒 Locked
                            </span>
                          </div>
                          <h4 className="mm-framework-name">{framework.name}</h4>
                          {framework.description && (
                            <p className="mm-framework-description">{framework.description}</p>
                          )}
                          {framework.missing && framework.missing.length > 0 && (
                            <div className="mm-unlock-hints">
                              <p className="mm-unlock-label">Share to unlock:</p>
                              <div className="mm-unlock-tags">
                                {framework.missing.slice(0, 3).map((need, idx) => (
                                  <span key={idx} className="mm-unlock-tag">
                                    {formatNeedLabel(need)}
                                  </span>
                                ))}
                                {framework.missing.length > 3 && (
                                  <span className="mm-unlock-tag">
                                    +{framework.missing.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {frameworks.length === 0 && !loading && (
                  <div className="mm-empty-state">
                    <div className="mm-empty-icon">🎯</div>
                    <h3 className="mm-empty-title">No frameworks available</h3>
                    <p className="mm-empty-text">Your coach hasn't uploaded any frameworks yet</p>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default FrameworkPickerModal;
